// src/components/product/ProductSpecificationSection.tsx

import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ProductUpsertRequest } from '@apis/product';

type ProductSpecification = ProductUpsertRequest['specification'];
type DataCapacity = ProductSpecification['dataCapacity'];

type Country = {
  countryId: number;
  englishName: string;
};

export interface ProductSpecificationSectionProps {
  specification: ProductSpecification;
  coverages: Country[];
  allCountries: Country[];
  onChangeSpecification: (
    name: keyof ProductSpecification,
    value: string | number | boolean | null,
  ) => void;
  onChangeSpecificationData: (
    name: keyof DataCapacity,
    value: string | number,
  ) => void;
  onAddCoverage: (country: Country) => void;
  onDeleteCoverage: (countryId: number) => void;
}

const DATA_UNITS = ['MB', 'GB', 'TB'] as const;
const REFRESH_UNITS = ['DAYS', 'WEEKS', 'MONTHS', 'YEARS'] as const;

/**
 * ProductSpecificationSection
 *
 * Edits product "specification" fields:
 * 1) Validity (dayCount)
 * 2) Data settings (quota, unit, post-quota behavior, optional refresh cycle)
 * 3) Voice call support
 * 4) Coverage countries (add/remove via Autocomplete + chips)
 *
 * UI safety guards:
 * - Deduplicates coverages by countryId to keep list rendering stable (unique React keys).
 * - Hides the refresh unit Select when refresh is disabled, because refreshPeriodUnit can be
 *   "NEVER" which is not a valid Select option.
 * - Keeps Autocomplete "value" controlled as null so the selection does not remain displayed
 *   after adding a country; only the query text is controlled.
 */
const ProductSpecificationSection = ({
  specification,
  coverages,
  allCountries,
  onChangeSpecification,
  onChangeSpecificationData,
  onAddCoverage,
  onDeleteCoverage,
}: ProductSpecificationSectionProps) => {
  const isUnlimited = specification.isUnlimitedData;
  const hasRefresh = specification.dataCapacity.refreshPeriodUnit !== 'NEVER';

  // Controls the Autocomplete input text only (not the selected option).
  const [countryQuery, setCountryQuery] = useState('');

  /**
   * Deduplicate coverages by countryId (defensive).
   * Using a Map avoids React duplicate-key warnings and makes the chip list deterministic
   * even if upstream data contains duplicates.
   *
   * NOTE:
   * - We intentionally avoid spreading Map.values() to keep compatibility with lower TS targets.
   */
  const sortedCoverages = useMemo(() => {
    const byId = new Map<number, Country>();

    for (const c of coverages) {
      if (!c?.countryId) continue;
      byId.set(c.countryId, c);
    }

    return Array.from(byId.values()).sort((a, b) =>
      a.englishName.localeCompare(b.englishName),
    );
  }, [coverages]);

  /**
   * Countries available to add:
   * allCountries - selectedCoverages (deduped).
   */
  const availableCountries = useMemo(() => {
    const selectedIds = new Set(sortedCoverages.map((c) => c.countryId));

    return allCountries
      .filter((c) => !selectedIds.has(c.countryId))
      .sort((a, b) => a.englishName.localeCompare(b.englishName));
  }, [allCountries, sortedCoverages]);

  const handleToggleUnlimited = (checked: boolean) => {
    onChangeSpecification('isUnlimitedData', checked);

    // When unlimited is enabled, reset quota/refresh fields to a neutral state.
    if (checked) {
      onChangeSpecificationData('quantity', 0);
      onChangeSpecificationData('unit', 'MB');
      onChangeSpecificationData('refreshPeriodUnit', 'NEVER');
      onChangeSpecificationData('refreshPeriodValue', 0);
    }
  };

  const handleToggleRefresh = (enabled: boolean) => {
    if (!enabled) {
      onChangeSpecificationData('refreshPeriodUnit', 'NEVER');
      onChangeSpecificationData('refreshPeriodValue', 0);
      return;
    }

    // When enabling refresh, initialize to a sensible default.
    onChangeSpecificationData('refreshPeriodUnit', 'DAYS');
    onChangeSpecificationData('refreshPeriodValue', 1);
  };

  const handleChangeQuantity = (raw: string) => {
    const val = Number(raw || 0);
    if (val < 0) return;
    onChangeSpecificationData('quantity', val);
  };

  const handleChangeDayCount = (raw: string) => {
    onChangeSpecification('dayCount', Number(raw || 0));
  };

  const handleChangeRefreshValue = (raw: string) => {
    const val = Number(raw || 0);
    if (val < 0) return;
    onChangeSpecificationData('refreshPeriodValue', val);
  };

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      })}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Specification
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Configure validity, data limit, FUP behavior, refresh cycle and country
            coverage.
          </Typography>
        }
      />

      <CardContent>
        <Stack spacing={3}>
          {/* 1) Validity ----------------------------------------------------- */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              1. Validity
            </Typography>

            <TextField
              label="Days"
              size="small"
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              value={specification.dayCount}
              onChange={(e) => handleChangeDayCount(e.target.value)}
              sx={{ maxWidth: 240 }}
              helperText="Number of days the plan can be used after activation."
            />
          </Box>

          {/* 2) Data limit --------------------------------------------------- */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              2. Data limit
            </Typography>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isUnlimited}
                    onChange={(e) => handleToggleUnlimited(e.target.checked)}
                  />
                }
                label="Unlimited data"
              />

              <Typography variant="caption" color="text.secondary">
                When unlimited data is enabled, data limit, FUP and refresh cycle do
                not apply.
              </Typography>

              {/* Data limit / FUP / refresh group */}
              <Box
                sx={{
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: (theme) =>
                    isUnlimited ? theme.palette.action.disabled : theme.palette.divider,
                  p: 2,
                  opacity: isUnlimited ? 0.6 : 1,
                }}
              >
                <Stack spacing={2}>
                  {/* Quota + unit */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Data limit
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ maxWidth: 260 }}>
                      <TextField
                        size="small"
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={specification.dataCapacity.quantity}
                        onChange={(e) => handleChangeQuantity(e.target.value)}
                        disabled={isUnlimited}
                        sx={{ width: 120 }}
                      />

                      <FormControl size="small" sx={{ minWidth: 80 }} disabled={isUnlimited}>
                        <InputLabel id="unit-label">Unit</InputLabel>
                        <Select
                          labelId="unit-label"
                          label="Unit"
                          value={specification.dataCapacity.unit}
                          onChange={(e) =>
                            onChangeSpecificationData('unit', e.target.value as string)
                          }
                        >
                          {DATA_UNITS.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      Total data provided (MB / GB / TB).
                    </Typography>
                  </Box>

                  {/* Cutoff vs FUP */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      After data limit is reached
                    </Typography>

                    <FormControl disabled={isUnlimited}>
                      <RadioGroup
                        row
                        value={specification.isFup ? 'FUP' : 'CUTOFF'}
                        onChange={(e) =>
                          onChangeSpecification('isFup', e.target.value === 'FUP')
                        }
                      >
                        <FormControlLabel
                          value="CUTOFF"
                          control={<Radio size="small" />}
                          label="Cut off data"
                        />
                        <FormControlLabel
                          value="FUP"
                          control={<Radio size="small" />}
                          label="Apply FUP (reduced speed)"
                        />
                      </RadioGroup>
                    </FormControl>

                    <Typography variant="caption" color="text.secondary">
                      Choose whether to stop data or apply reduced speed after the limit is reached.
                    </Typography>
                  </Box>

                  {/* Refresh cycle */}
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                      Refresh cycle
                    </Typography>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hasRefresh}
                          onChange={(e) => handleToggleRefresh(e.target.checked)}
                        />
                      }
                      disabled={isUnlimited}
                      label="Reset data on a regular cycle"
                    />

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ mt: 1 }}
                    >
                      <TextField
                        label="Every"
                        size="small"
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        value={specification.dataCapacity.refreshPeriodValue}
                        onChange={(e) => handleChangeRefreshValue(e.target.value)}
                        disabled={isUnlimited || !hasRefresh}
                        sx={{ maxWidth: 120 }}
                      />

                      {/* Render only when enabled to avoid out-of-range value "NEVER". */}
                      {hasRefresh ? (
                        <FormControl size="small" sx={{ minWidth: 160 }} disabled={isUnlimited}>
                          <InputLabel id="refresh-unit-label">Unit</InputLabel>
                          <Select
                            labelId="refresh-unit-label"
                            label="Unit"
                            value={specification.dataCapacity.refreshPeriodUnit}
                            onChange={(e) =>
                              onChangeSpecificationData(
                                'refreshPeriodUnit',
                                e.target.value as string,
                              )
                            }
                          >
                            {REFRESH_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : null}
                    </Stack>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      Data resets on this interval. Turn it off to keep a fixed quota for the entire
                      validity period.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* 3) Voice call support ------------------------------------------- */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              3. Voice call support
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={specification.isVoiceCallSupported}
                  onChange={(e) =>
                    onChangeSpecification('isVoiceCallSupported', e.target.checked)
                  }
                />
              }
              label={
                specification.isVoiceCallSupported
                  ? 'Voice calls included in this product'
                  : 'Data only (no voice calls)'
              }
            />

            <Typography variant="caption" color="text.secondary">
              Enable if the plan includes voice minutes in addition to data.
            </Typography>
          </Box>

          <Divider />

          {/* 4) Coverage ------------------------------------------------------ */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Coverage
            </Typography>

            {/* Selected coverage chips */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: 1,
                rowGap: 1.2,
                mb: 2,
              }}
            >
              {sortedCoverages.map((c) => (
                <Chip
                  key={c.countryId}
                  label={c.englishName}
                  onDelete={() => onDeleteCoverage(c.countryId)}
                  sx={{ maxWidth: 220 }}
                />
              ))}

              {sortedCoverages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No countries selected.
                </Typography>
              ) : null}
            </Box>

            {/* Add coverage via Autocomplete */}
            <Autocomplete
              value={null}
              inputValue={countryQuery}
              onInputChange={(_, next, reason) => {
                // Keep the input fully controlled, but avoid "re-applying" a value after selection.
                // - "input": user typing
                // - "clear"/"reset": MUI internally clears the input (e.g., after selection or ESC)
                if (reason === 'input') setCountryQuery(next);
                if (reason === 'clear' || reason === 'reset') setCountryQuery('');
              }}
              options={availableCountries}
              getOptionLabel={(opt) => opt.englishName}
              isOptionEqualToValue={(opt, val) => opt.countryId === val.countryId}
              onChange={(_, selected) => {
                if (!selected) return;
                onAddCoverage(selected);
                setCountryQuery('');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add country"
                  placeholder="Search and select a country"
                  size="small"
                />
              )}
              sx={{ maxWidth: 360 }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.5, display: 'block' }}
            >
              Select countries where this product can be used.
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductSpecificationSection;
